import { useRef, useState } from "react";
import type {
  CmmdBoardTask,
  CmmdProjectAgentInstance,
  CmmdProjectAgentMember,
  CmmdProjectLeadEvents,
} from "@cmmd-forge/contracts";

import {
  AgentIdentityBadge,
  AgentKindBadge,
  AgentMemberAvatar,
} from "~/components/identity/AgentMemberIdentity";
import {
  Sheet,
  SheetDescription,
  SheetHeader,
  SheetPanel,
  SheetPopup,
  SheetTitle,
} from "~/components/ui/sheet";
import { AgentMemberRetireDialog } from "./AgentMemberRetireDialog";
import { AgentMemberInspectorBody } from "./AgentMemberInspectorBody";

export function AgentMemberDetails(props: {
  readonly member: CmmdProjectAgentMember | null;
  readonly tasks: readonly CmmdBoardTask[];
  readonly instances: readonly CmmdProjectAgentInstance[];
  readonly historyLoading: boolean;
  readonly historyError: string | null;
  readonly events: CmmdProjectLeadEvents["events"];
  readonly eventsLoading: boolean;
  readonly eventsError: string | null;
  readonly onClose: () => void;
  readonly onRename: (memberId: number, displayName: string) => Promise<void>;
  readonly onRetire: (memberId: number) => Promise<void>;
  readonly onUpdateAvatar: (
    memberId: number,
    avatar: { readonly kind: "generated"; readonly seed: string } | { readonly kind: "default" },
  ) => Promise<void>;
}) {
  const member = props.member;
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);
  const [retireOpen, setRetireOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const inspectorRef = useRef<HTMLDivElement>(null);
  if (!member) return null;
  const work = props.tasks.filter(
    (task) =>
      task.assignment?.kind === "agent_member" && task.assignment.agentMemberId === member.id,
  );
  const rename = async () => {
    if (!name.trim()) return;
    setSaving(true);
    setError(null);
    try {
      await props.onRename(member.id, name.trim());
      setName("");
    } catch (cause) {
      setError(formatAgentMemberMutationError(cause, "Agent Member could not be renamed."));
    } finally {
      setSaving(false);
    }
  };
  const retire = async () => {
    setSaving(true);
    setError(null);
    try {
      await props.onRetire(member.id);
      setRetireOpen(false);
    } catch (cause) {
      setError(formatAgentMemberMutationError(cause, "Agent Member could not be retired."));
    } finally {
      setSaving(false);
    }
  };
  return (
    <Sheet
      open
      onOpenChange={(open) => {
        if (!open) props.onClose();
      }}
    >
      <SheetPopup
        ref={inspectorRef}
        initialFocus={inspectorRef}
        className="max-w-2xl outline-none motion-reduce:transition-none"
        side="right"
        tabIndex={-1}
      >
        <SheetHeader className="border-b border-border px-5 py-4 pr-14">
          <div className="flex items-center gap-3">
            <AgentMemberAvatar member={member} className="size-11" />
            <div className="min-w-0">
              <SheetTitle className="flex flex-wrap items-center gap-2 text-base">
                {member.displayName}
                <AgentIdentityBadge />
                <AgentKindBadge kind={member.agentKind} />
              </SheetTitle>
              <SheetDescription className="mt-1 truncate font-mono text-xs">
                {member.agentId}
              </SheetDescription>
            </div>
          </div>
        </SheetHeader>
        <SheetPanel className="px-5 py-0">
          <AgentMemberInspectorBody
            member={member}
            tasks={work}
            instances={props.instances}
            historyLoading={props.historyLoading}
            historyError={props.historyError}
            events={props.events}
            eventsLoading={props.eventsLoading}
            eventsError={props.eventsError}
            saving={saving}
            error={error}
            name={name}
            onNameChange={setName}
            onRename={() => void rename()}
            onRetire={() => setRetireOpen(true)}
            onUpdateAvatar={async (avatar) => {
              setSaving(true);
              setError(null);
              try {
                await props.onUpdateAvatar(member.id, avatar);
              } catch (cause) {
                setError(formatAgentMemberMutationError(cause, "Avatar could not be saved."));
                throw cause;
              } finally {
                setSaving(false);
              }
            }}
          />
        </SheetPanel>
      </SheetPopup>
      <AgentMemberRetireDialog
        displayName={member.displayName}
        open={retireOpen}
        pending={saving}
        onOpenChange={setRetireOpen}
        onConfirm={retire}
      />
    </Sheet>
  );
}

export function formatAgentMemberMutationError(cause: unknown, fallback: string): string {
  const message = cause instanceof Error ? cause.message : typeof cause === "string" ? cause : "";
  return /<\/?(?:!doctype|html|head|body|pre)\b/i.test(message) ||
    /Cannot (?:PUT|POST|PATCH)\s+\/api\//i.test(message)
    ? "This action is not available in the current CMMD deployment yet."
    : message.trim() || fallback;
}
